import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { graphBuilderApi, apiService } from '../../services/api';
import BuildGraphMenu from '../UserHome/BuildGraphMenu';
import LinkSuggestionPanel from './LinkSuggestionPanel';
import './GraphLinksPage.css';

function GraphLinksPage() {
  const { graphId } = useParams();

  const [knowledgeGraphs, setKnowledgeGraphs] = useState([]);
  const [processDefinitions, setProcessDefinitions] = useState([]);
  const [selectedKG, setSelectedKG] = useState(graphId || '');
  const [selectedProcess, setSelectedProcess] = useState('');

  const [knowledgeNodes, setKnowledgeNodes] = useState([]);
  const [processNodes, setProcessNodes] = useState([]);
  const [links, setLinks] = useState([]);
  const [linkSummary, setLinkSummary] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSuggestionPanel, setShowSuggestionPanel] = useState(false);
  const [kgSearch, setKgSearch] = useState('');
  const [pnSearch, setPnSearch] = useState('');

  // Fetch available graphs and processes
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [graphsResp, processResp] = await Promise.all([
          apiService.listGraphs(),
          graphBuilderApi.listProcessDefinitions({}),
        ]);
        setKnowledgeGraphs(
          (graphsResp?.graphs || graphsResp || []).filter(
            (g) => (g.graphType || g.GraphType || '') !== 'Router'
          )
        );
        setProcessDefinitions(processResp?.processes || processResp || []);
      } catch (err) {
        console.error('Failed to load options:', err);
      }
    };
    fetchOptions();
  }, []);

  // Fetch knowledge nodes when KG is selected
  useEffect(() => {
    if (!selectedKG) { setKnowledgeNodes([]); return; }
    const fetchNodes = async () => {
      try {
        const resp = await apiService.listNodes(selectedKG, { limit: 500 });
        setKnowledgeNodes(resp?.nodes || resp || []);
      } catch (err) {
        console.error('Failed to load nodes:', err);
        setKnowledgeNodes([]);
      }
    };
    fetchNodes();
  }, [selectedKG]);

  // Fetch process nodes when process is selected
  useEffect(() => {
    if (!selectedProcess) { setProcessNodes([]); return; }
    const fetchProcDef = async () => {
      try {
        const resp = await graphBuilderApi.getProcessDefinition(selectedProcess);
        const def = resp?.definition || resp?.Definition || {};
        const nodes = def?.nodes || [];
        setProcessNodes(nodes);
      } catch (err) {
        console.error('Failed to load process definition:', err);
        setProcessNodes([]);
      }
    };
    fetchProcDef();
  }, [selectedProcess]);

  // Fetch links when both sides are selected
  const fetchLinks = useCallback(async () => {
    if (!selectedKG || !selectedProcess) { setLinks([]); return; }
    try {
      setLoading(true);
      const resp = await graphBuilderApi.listGraphLinks({
        knowledge_graph_id: selectedKG,
        process_id: selectedProcess,
      });
      setLinks(resp?.links || []);
    } catch (err) {
      console.error('Failed to load links:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedKG, selectedProcess]);

  useEffect(() => { fetchLinks(); }, [fetchLinks]);

  // Fetch summary
  useEffect(() => {
    if (!selectedProcess) { setLinkSummary(null); return; }
    graphBuilderApi.getGraphLinkSummary({ process_id: selectedProcess })
      .then(setLinkSummary)
      .catch(() => setLinkSummary(null));
  }, [selectedProcess, links]);

  const handleCreateLink = async (knowledgeNodeId, processNodeId) => {
    try {
      setError(null);
      await graphBuilderApi.createGraphLink({
        workspace_id: null,
        knowledge_graph_id: selectedKG,
        knowledge_node_id: knowledgeNodeId,
        process_id: selectedProcess,
        process_node_id: processNodeId,
        link_direction: 'bidirectional',
      });
      fetchLinks();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const handleDeleteLink = async (linkId) => {
    try {
      await graphBuilderApi.deleteGraphLink(linkId);
      fetchLinks();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const handleSuggestionsAccepted = async (suggestions) => {
    try {
      setError(null);
      const linkData = suggestions.map((s) => ({
        knowledge_graph_id: selectedKG,
        knowledge_node_id: s.knowledge_node_id,
        process_id: selectedProcess,
        process_node_id: s.process_node_id,
        link_direction: s.direction || 'bidirectional',
        link_purpose: s.purpose,
      }));
      await graphBuilderApi.bulkCreateGraphLinks(linkData);
      fetchLinks();
      setShowSuggestionPanel(false);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const filteredKGNodes = knowledgeNodes.filter((n) => {
    if (!kgSearch) return true;
    const term = kgSearch.toLowerCase();
    return (
      (n.nodeName || n.NodeName || '').toLowerCase().includes(term) ||
      (n.nodeType || n.NodeType || '').toLowerCase().includes(term) ||
      (n.description || n.Description || '').toLowerCase().includes(term)
    );
  });

  const filteredProcNodes = processNodes.filter((n) => {
    if (!pnSearch) return true;
    const term = pnSearch.toLowerCase();
    return (
      (n.name || '').toLowerCase().includes(term) ||
      (n.description || '').toLowerCase().includes(term) ||
      (n.node_id || '').toLowerCase().includes(term)
    );
  });

  const linkedPairs = new Set(
    links.map((l) => `${l.KnowledgeNodeId || l.knowledge_node_id}::${l.ProcessNodeId || l.process_node_id}`)
  );

  const getNodeLinkCount = (processNodeId) => {
    if (!linkSummary?.nodes) return 0;
    return linkSummary.nodes[processNodeId]?.link_count || 0;
  };

  return (
    <div className="graph-links-page">
      {graphId && <BuildGraphMenu graphId={graphId} />}

      <div className="graph-links-header">
        <h2>Graph Links</h2>
        <p className="graph-links-subtitle">
          Connect knowledge graph nodes with process graph steps
        </p>
      </div>

      {error && (
        <div className="graph-links-error">
          {error}
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      <div className="graph-links-selectors">
        <div className="selector-group">
          <label>Knowledge Graph</label>
          <select value={selectedKG} onChange={(e) => setSelectedKG(e.target.value)}>
            <option value="">-- Select a knowledge graph --</option>
            {knowledgeGraphs.map((g) => (
              <option key={g.graphId || g.GraphId} value={g.graphId || g.GraphId}>
                {g.graphName || g.GraphName}
              </option>
            ))}
          </select>
        </div>
        <div className="selector-group">
          <label>Process Definition</label>
          <select value={selectedProcess} onChange={(e) => setSelectedProcess(e.target.value)}>
            <option value="">-- Select a process --</option>
            {processDefinitions.map((p) => (
              <option key={p.process_id} value={p.process_id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        {selectedKG && selectedProcess && (
          <button
            className="suggest-btn"
            onClick={() => setShowSuggestionPanel(true)}
          >
            AI Suggest Links
          </button>
        )}
      </div>

      {selectedKG && selectedProcess && (
        <div className="graph-links-workspace">
          {/* Left panel: Knowledge nodes */}
          <div className="links-panel kg-panel">
            <div className="panel-header">
              <h3>Knowledge Nodes ({filteredKGNodes.length})</h3>
              <input
                type="text"
                placeholder="Search nodes..."
                value={kgSearch}
                onChange={(e) => setKgSearch(e.target.value)}
                className="panel-search"
              />
            </div>
            <div className="panel-list">
              {filteredKGNodes.map((node) => {
                const nodeId = node.nodeId || node.NodeId;
                const hasLinks = links.some(
                  (l) => (l.KnowledgeNodeId || l.knowledge_node_id) === nodeId
                );
                return (
                  <div
                    key={nodeId}
                    className={`node-card ${hasLinks ? 'linked' : ''}`}
                  >
                    <div className="node-card-header">
                      <span className="node-name">{node.nodeName || node.NodeName}</span>
                      <span className="node-type-badge">
                        {node.nodeType || node.NodeType || 'concept'}
                      </span>
                    </div>
                    <div className="node-card-desc">
                      {(node.description || node.Description || '').slice(0, 120)}
                    </div>
                    {hasLinks && (
                      <div className="node-link-indicator">
                        {links.filter(
                          (l) => (l.KnowledgeNodeId || l.knowledge_node_id) === nodeId
                        ).length}{' '}
                        link(s)
                      </div>
                    )}
                  </div>
                );
              })}
              {filteredKGNodes.length === 0 && (
                <div className="empty-panel">No nodes found</div>
              )}
            </div>
          </div>

          {/* Center: Links */}
          <div className="links-panel links-center">
            <div className="panel-header">
              <h3>Active Links ({links.length})</h3>
            </div>
            <div className="panel-list">
              {loading ? (
                <div className="empty-panel">Loading...</div>
              ) : links.length === 0 ? (
                <div className="empty-panel">
                  No links yet. Select nodes from both panels or use AI Suggest.
                </div>
              ) : (
                links.map((link) => {
                  const linkId = link.GraphLinkId || link.graph_link_id;
                  return (
                    <div key={linkId} className="link-card">
                      <div className="link-card-nodes">
                        <span className="link-node-name">
                          {link.NodeName || link.node_name || '?'}
                        </span>
                        <span className="link-direction-arrow">
                          {(link.LinkDirection || link.link_direction) === 'knowledge_to_process'
                            ? '→'
                            : (link.LinkDirection || link.link_direction) === 'process_to_knowledge'
                              ? '←'
                              : '↔'}
                        </span>
                        <span className="link-node-name">
                          {link.ProcessNodeId || link.process_node_id}
                        </span>
                      </div>
                      {(link.LinkPurpose || link.link_purpose) && (
                        <div className="link-purpose">
                          {link.LinkPurpose || link.link_purpose}
                        </div>
                      )}
                      <button
                        className="link-delete-btn"
                        onClick={() => handleDeleteLink(linkId)}
                        title="Remove link"
                      >
                        ×
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Right panel: Process nodes */}
          <div className="links-panel pn-panel">
            <div className="panel-header">
              <h3>Process Nodes ({filteredProcNodes.length})</h3>
              <input
                type="text"
                placeholder="Search steps..."
                value={pnSearch}
                onChange={(e) => setPnSearch(e.target.value)}
                className="panel-search"
              />
            </div>
            <div className="panel-list">
              {filteredProcNodes.map((node) => {
                const linkCount = getNodeLinkCount(node.node_id);
                return (
                  <div
                    key={node.node_id}
                    className={`node-card ${linkCount > 0 ? 'linked' : ''}`}
                  >
                    <div className="node-card-header">
                      <span className="node-name">{node.name}</span>
                      <span className="node-type-badge">
                        {node.execution_type || 'llm'}
                      </span>
                    </div>
                    <div className="node-card-desc">
                      {(node.description || '').slice(0, 120)}
                    </div>
                    {linkCount > 0 && (
                      <div className="node-link-indicator">
                        {linkCount} link(s)
                      </div>
                    )}
                  </div>
                );
              })}
              {filteredProcNodes.length === 0 && (
                <div className="empty-panel">No process nodes found</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quick link creator */}
      {selectedKG && selectedProcess && (
        <QuickLinkCreator
          knowledgeNodes={filteredKGNodes}
          processNodes={filteredProcNodes}
          linkedPairs={linkedPairs}
          onCreateLink={handleCreateLink}
        />
      )}

      {showSuggestionPanel && selectedKG && selectedProcess && (
        <LinkSuggestionPanel
          knowledgeGraphId={selectedKG}
          processId={selectedProcess}
          onAccept={handleSuggestionsAccepted}
          onClose={() => setShowSuggestionPanel(false)}
        />
      )}
    </div>
  );
}

function QuickLinkCreator({ knowledgeNodes, processNodes, linkedPairs, onCreateLink }) {
  const [selectedKN, setSelectedKN] = useState('');
  const [selectedPN, setSelectedPN] = useState('');

  const handleCreate = () => {
    if (selectedKN && selectedPN) {
      onCreateLink(selectedKN, selectedPN);
      setSelectedKN('');
      setSelectedPN('');
    }
  };

  const isAlreadyLinked = selectedKN && selectedPN && linkedPairs.has(`${selectedKN}::${selectedPN}`);

  return (
    <div className="quick-link-creator">
      <h4>Quick Link</h4>
      <div className="quick-link-row">
        <select value={selectedKN} onChange={(e) => setSelectedKN(e.target.value)}>
          <option value="">-- Knowledge Node --</option>
          {knowledgeNodes.map((n) => (
            <option key={n.nodeId || n.NodeId} value={n.nodeId || n.NodeId}>
              {n.nodeName || n.NodeName}
            </option>
          ))}
        </select>
        <span className="quick-link-arrow">↔</span>
        <select value={selectedPN} onChange={(e) => setSelectedPN(e.target.value)}>
          <option value="">-- Process Node --</option>
          {processNodes.map((n) => (
            <option key={n.node_id} value={n.node_id}>
              {n.name}
            </option>
          ))}
        </select>
        <button
          className="quick-link-btn"
          onClick={handleCreate}
          disabled={!selectedKN || !selectedPN || isAlreadyLinked}
        >
          {isAlreadyLinked ? 'Already Linked' : 'Create Link'}
        </button>
      </div>
    </div>
  );
}

export default GraphLinksPage;
